import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import type { EnrichedProject, CallBudgetStatus, KbStats } from "../types/index.js";
import { apiGet, apiPost, apiPut, apiDelete } from "../lib/api.js";
import FolderExplorerModal from "./FolderExplorerModal.js";

export interface ProjectSettingsPanelProps {
	project: EnrichedProject;
	/** Bounding rect of the trigger element, used to position the panel. */
	anchorRect: DOMRect;
	onClose: () => void;
	/** Called after a sync request is successfully submitted (202 response). */
	onSyncTriggered: () => void;
	/** Called after the folder is successfully changed and re-ingestion triggered. */
	onFolderChanged: () => void;
	/** Called after the folder link is successfully removed. */
	onFolderUnlinked: () => void;
	/** ISO timestamp of the last successful ingestion, or null if never synced. */
	lastIngestedAt: string | null;
	/** Whether the logged-in user is an admin. */
	isAdmin?: boolean;
	/**
	 * Called immediately when a sync POST is about to be sent, so the parent
	 * can show an instant "Starting sync…" indicator.
	 */
	onSyncStarting?: () => void;
	/**
	 * Called when the sync POST fails (non-ok response or network error), so
	 * the parent can clear the "Starting sync…" indicator.
	 */
	onSyncFailed?: () => void;
}

/**
 * Returns a human-readable relative time string for the given ISO timestamp.
 * Examples: "just now", "5 minutes ago", "2 hours ago", "3 days ago".
 */
function formatRelativeTime(isoTimestamp: string): string {
	const then = new Date(isoTimestamp).getTime();
	if (isNaN(then)) return "unknown";

	const diffSeconds = Math.floor((Date.now() - then) / 1000);
	if (diffSeconds < 0) return "just now";
	if (diffSeconds < 60) return "just now";
	if (diffSeconds < 3600) {
		const mins = Math.floor(diffSeconds / 60);
		return `${mins} ${mins === 1 ? "minute" : "minutes"} ago`;
	}
	if (diffSeconds < 86400) {
		const hours = Math.floor(diffSeconds / 3600);
		return `${hours} ${hours === 1 ? "hour" : "hours"} ago`;
	}
	const days = Math.floor(diffSeconds / 86400);
	return `${days} ${days === 1 ? "day" : "days"} ago`;
}

/**
 * Inline confirmation state — which destructive action is awaiting confirmation.
 * null means no confirmation is active.
 */
type ConfirmAction = "change" | "unlink" | "reingest" | null;

/**
 * Status message displayed below an action button.
 */
interface StatusMessage {
	type: "info" | "error" | "success";
	text: string;
}

/**
 * Project settings dropdown panel.
 *
 * Rendered via React Portal so it floats above all other content.
 * Anchored near the trigger element using fixed positioning based on anchorRect.
 * Supports three actions: Sync Now, Change Folder, Unlink Folder.
 * Destructive actions use inline confirmation rather than window.confirm().
 */
export default function ProjectSettingsPanel({
	project,
	anchorRect,
	onClose,
	onSyncTriggered,
	onFolderChanged,
	onFolderUnlinked,
	lastIngestedAt,
	isAdmin = false,
	onSyncStarting,
	onSyncFailed,
}: ProjectSettingsPanelProps) {
	/** Which destructive action is awaiting confirmation. */
	const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null);

	/** Whether a WorkDrive document sync request is currently in-flight. */
	const [isSyncing, setIsSyncing] = useState(false);

	/** Whether a project entity sync request is currently in-flight. */
	const [isSyncingEntities, setIsSyncingEntities] = useState(false);

	/** Call budget status fetched from the backend. Null while loading or unavailable. */
	const [callBudget, setCallBudget] = useState<CallBudgetStatus | null>(null);

	/** Knowledge base statistics fetched from the backend. Null while loading. */
	const [kbStats, setKbStats] = useState<KbStats | null>(null);

	/** Whether kb-stats are currently being fetched. */
	const [isLoadingKbStats, setIsLoadingKbStats] = useState(false);

	/** Whether a folder-change operation is in-flight. */
	const [isChangingFolder, setIsChangingFolder] = useState(false);

	/** Whether an unlink operation is in-flight. */
	const [isUnlinking, setIsUnlinking] = useState(false);

	/** Whether a re-ingest-all operation is in-flight. */
	const [isReingesting, setIsReingesting] = useState(false);

	/** Whether a retry-failed-files operation is in-flight. */
	const [isRetryingFailed, setIsRetryingFailed] = useState(false);

	/** Whether the folder explorer modal is open for the Change Folder flow. */
	const [showFolderExplorer, setShowFolderExplorer] = useState(false);

	/** Resolved name of the currently linked WorkDrive folder. Null while loading or if no folder linked. */
	const [linkedFolderName, setLinkedFolderName] = useState<string | null>(null);

	/** Status message displayed to the user (success, error, or info). */
	const [status, setStatus] = useState<StatusMessage | null>(null);

	const panelRef = useRef<HTMLDivElement>(null);

	/* ------------------------------------------------------------------ */
	/*  Position calculation                                                */
	/* ------------------------------------------------------------------ */

	/**
	 * Position the panel below and aligned to the right edge of the anchor.
	 * Clamp to viewport so it doesn't clip off-screen.
	 */
	const panelWidth = 300;
	let left = anchorRect.right - panelWidth;
	let top = anchorRect.bottom + 6;

	// Clamp to viewport with 8px margin
	if (left < 8) left = 8;
	if (left + panelWidth > window.innerWidth - 8) {
		left = window.innerWidth - 8 - panelWidth;
	}
	// Use a generous estimated height to account for all sections added since Phase 8G-H.
	// The CSS max-height clamps the actual rendered size; this value determines when to flip above.
	const estimatedPanelHeight = 480;
	if (top + estimatedPanelHeight > window.innerHeight - 8) {
		// Flip above the anchor if it would clip the bottom
		top = anchorRect.top - estimatedPanelHeight - 6;
	}

	/* ------------------------------------------------------------------ */
	/*  Click-outside and Escape handling                                   */
	/* ------------------------------------------------------------------ */

	useEffect(() => {
		if (showFolderExplorer) return; // Modal handles its own events

		function handleMouseDown(event: MouseEvent): void {
			if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
				onClose();
			}
		}

		function handleKeyDown(event: KeyboardEvent): void {
			if (event.key === "Escape") {
				onClose();
			}
		}

		document.addEventListener("mousedown", handleMouseDown);
		document.addEventListener("keydown", handleKeyDown);
		return () => {
			document.removeEventListener("mousedown", handleMouseDown);
			document.removeEventListener("keydown", handleKeyDown);
		};
	}, [onClose, showFolderExplorer]);

	/* ------------------------------------------------------------------ */
	/*  Fetch call budget on mount                                         */
	/* ------------------------------------------------------------------ */

	useEffect(() => {
		async function fetchCallBudget(): Promise<void> {
			try {
				const response = await apiGet("/api/webhooks/budget");
				if (response.ok) {
					const data = await response.json() as CallBudgetStatus;
					// Only surface the budget if calls are actually being made
					if (data.count > 0) {
						setCallBudget(data);
					}
				}
			} catch {
				// Budget is informational — silently ignore errors
			}
		}
		void fetchCallBudget();
	}, []);

	/* ------------------------------------------------------------------ */
	/*  Fetch knowledge base stats                                          */
	/* ------------------------------------------------------------------ */

	async function fetchKbStats(): Promise<void> {
		if (!project.supabaseId) return;
		setIsLoadingKbStats(true);
		try {
			const response = await apiGet(`/api/projects/${project.id}/kb-stats`);
			if (response.ok) {
				const data = await response.json() as KbStats;
				setKbStats(data);
			}
		} catch {
			// KB stats are informational — silently ignore errors
		} finally {
			setIsLoadingKbStats(false);
		}
	}

	useEffect(() => {
		void fetchKbStats();
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [project.id]);

	/* ------------------------------------------------------------------ */
	/*  Fetch linked folder name                                            */
	/* ------------------------------------------------------------------ */

	useEffect(() => {
		if (!project.workdriveFolderId) {
			setLinkedFolderName(null);
			return;
		}

		async function fetchFolderName(): Promise<void> {
			if (!project.workdriveFolderId) return;
			try {
				const response = await apiGet(`/api/workdrive/folder-info/${project.workdriveFolderId}`);
				if (response.ok) {
					const data = await response.json() as { id: string; name: string };
					setLinkedFolderName(data.name);
				}
			} catch {
				// Non-critical — silently ignore; folder ID is always available as fallback
			}
		}

		void fetchFolderName();
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [project.workdriveFolderId]);

	/* ------------------------------------------------------------------ */
	/*  Sync Now (WorkDrive documents)                                      */
	/* ------------------------------------------------------------------ */

	async function handleSyncNow(): Promise<void> {
		if (isSyncing) return;

		setIsSyncing(true);
		setStatus(null);

		try {
			// Signal immediately so the parent can show a "Starting sync…" banner
			// before the POST completes.
			onSyncStarting?.();

			const response = await apiPost(`/api/projects/${project.id}/ingest`, {});
			if (response.status === 202 || response.ok) {
				setStatus({ type: "success", text: "Sync started" });
				void fetchKbStats();
				setTimeout(() => {
					onSyncTriggered();
				}, 1200);
			} else {
				const errorData = await response.json().catch(() => null) as { message?: string } | null;
				// Let the parent clear the "Starting sync…" indicator on failure.
				onSyncFailed?.();
				setStatus({
					type: "error",
					text: errorData?.message ?? `Sync failed (${response.status})`,
				});
			}
		} catch {
			// Let the parent clear the "Starting sync…" indicator on network error.
			onSyncFailed?.();
			setStatus({ type: "error", text: "Failed to connect to the server." });
		} finally {
			setIsSyncing(false);
		}
	}

	/* ------------------------------------------------------------------ */
	/*  Retry Failed Files                                                 */
	/* ------------------------------------------------------------------ */

	async function handleRetryFailed(): Promise<void> {
		if (isRetryingFailed) return;

		setIsRetryingFailed(true);
		setStatus(null);

		try {
			const response = await apiPost(`/api/projects/${project.id}/retry-failed`, {});
			if (response.ok || response.status === 202) {
				const data = await response.json().catch(() => null) as { retriedCount?: number; skippedPermanent?: number; message?: string } | null;
				const count = data?.retriedCount ?? 0;
				const skipped = data?.skippedPermanent ?? 0;
				const skippedSuffix = skipped > 0 ? ` (${skipped} permanent failure${skipped === 1 ? "" : "s"} skipped)` : "";
				setStatus({
					type: count > 0 ? "success" : "success",
					text: count === 0
						? (data?.message ?? "No failed files to retry")
						: `${count} ${count === 1 ? "file" : "files"} queued for retry${skippedSuffix}`,
				});
				void fetchKbStats();
				setTimeout(() => {
					setStatus(null);
				}, 3000);
			} else {
				const errorData = await response.json().catch(() => null) as { message?: string } | null;
				setStatus({
					type: "error",
					text: errorData?.message ?? `Retry failed (${response.status})`,
				});
			}
		} catch {
			setStatus({ type: "error", text: "Failed to connect to the server." });
		} finally {
			setIsRetryingFailed(false);
		}
	}

	/* ------------------------------------------------------------------ */
	/*  Sync Project Data (entities: tasks, milestones, etc.)              */
	/* ------------------------------------------------------------------ */

	async function handleSyncEntities(): Promise<void> {
		if (isSyncingEntities) return;

		setIsSyncingEntities(true);
		setStatus(null);

		try {
			const response = await apiPost(`/api/projects/${project.id}/sync-entities`, {});
			if (response.ok || response.status === 202) {
				setStatus({ type: "success", text: "Project data synced" });
				setTimeout(() => {
					setStatus(null);
				}, 2500);
			} else {
				const errorData = await response.json().catch(() => null) as { message?: string } | null;
				setStatus({
					type: "error",
					text: errorData?.message ?? `Sync failed (${response.status})`,
				});
			}
		} catch {
			setStatus({ type: "error", text: "Failed to connect to the server." });
		} finally {
			setIsSyncingEntities(false);
		}
	}

	/* ------------------------------------------------------------------ */
	/*  Change Folder                                                       */
	/* ------------------------------------------------------------------ */

	function handleChangeFolderRequest(): void {
		setConfirmAction("change");
		setStatus(null);
	}

	function handleChangeFolderConfirm(): void {
		setConfirmAction(null);
		setShowFolderExplorer(true);
	}

	/**
	 * Called when the user selects a folder in the explorer.
	 * Flow: PUT folder (validate) → DELETE documents (purge) → POST ingest → done.
	 */
	async function handleFolderSelected(folderId: string): Promise<void> {
		setShowFolderExplorer(false);
		setIsChangingFolder(true);
		setStatus({ type: "info", text: "Updating folder link…" });

		try {
			// Step 1: Update the folder link (validates the folder exists)
			const putResponse = await apiPut(`/api/projects/${project.id}/folder`, { folderId });
			if (!putResponse.ok) {
				const errorData = await putResponse.json().catch(() => null) as { message?: string } | null;
				setStatus({
					type: "error",
					text: errorData?.message ?? `Failed to update folder (${putResponse.status})`,
				});
				return;
			}

			// Step 2: Purge old indexed documents
			setStatus({ type: "info", text: "Removing old documents…" });
			const deleteResponse = await apiDelete(`/api/projects/${project.id}/documents`);
			if (!deleteResponse.ok) {
				// Log but continue — purge failure should not block re-ingestion
				const errorData = await deleteResponse.json().catch(() => null) as { message?: string } | null;
				setStatus({
					type: "error",
					text: errorData?.message ?? "Failed to remove old documents.",
				});
				return;
			}

			// Step 3: Trigger re-ingestion from the new folder
			setStatus({ type: "info", text: "Starting re-ingestion…" });
			const ingestResponse = await apiPost(`/api/projects/${project.id}/ingest`, {});
			if (!ingestResponse.ok && ingestResponse.status !== 202) {
				const errorData = await ingestResponse.json().catch(() => null) as { message?: string } | null;
				setStatus({
					type: "error",
					text: errorData?.message ?? "Folder updated but ingestion failed to start.",
				});
				return;
			}

			setStatus({ type: "success", text: "Folder changed — re-indexing started" });
			setTimeout(() => {
				onFolderChanged();
			}, 1200);
		} catch {
			setStatus({ type: "error", text: "Failed to connect to the server." });
		} finally {
			setIsChangingFolder(false);
		}
	}

	/* ------------------------------------------------------------------ */
	/*  Unlink Folder                                                       */
	/* ------------------------------------------------------------------ */

	function handleUnlinkRequest(): void {
		setConfirmAction("unlink");
		setStatus(null);
	}

	async function handleUnlinkConfirm(): Promise<void> {
		setConfirmAction(null);
		setIsUnlinking(true);
		setStatus({ type: "info", text: "Removing folder link…" });

		try {
			const response = await apiDelete(`/api/projects/${project.id}/folder`);
			if (response.ok) {
				setStatus({ type: "success", text: "Folder unlinked" });
				setTimeout(() => {
					onFolderUnlinked();
				}, 1000);
			} else {
				const errorData = await response.json().catch(() => null) as { message?: string } | null;
				setStatus({
					type: "error",
					text: errorData?.message ?? `Failed to unlink folder (${response.status})`,
				});
			}
		} catch {
			setStatus({ type: "error", text: "Failed to connect to the server." });
		} finally {
			setIsUnlinking(false);
		}
	}

	/* ------------------------------------------------------------------ */
	/*  Re-ingest All Documents                                            */
	/* ------------------------------------------------------------------ */

	function handleReingestRequest(): void {
		setConfirmAction("reingest");
		setStatus(null);
	}

	async function handleReingestConfirm(): Promise<void> {
		setConfirmAction(null);
		setIsReingesting(true);
		setStatus({ type: "info", text: "Starting re-ingestion…" });

		try {
			const response = await apiPost(`/api/projects/${project.id}/reingest`, {});
			if (response.ok || response.status === 202) {
				setStatus({ type: "success", text: "Re-ingestion started — this may take several minutes" });
				setTimeout(() => {
					onSyncTriggered();
				}, 1500);
				setTimeout(() => {
					setStatus(null);
				}, 3500);
			} else {
				const errorData = await response.json().catch(() => null) as { message?: string } | null;
				setStatus({
					type: "error",
					text: errorData?.message ?? `Re-ingestion failed (${response.status})`,
				});
			}
		} catch {
			setStatus({ type: "error", text: "Failed to connect to the server." });
		} finally {
			setIsReingesting(false);
		}
	}

	/* ------------------------------------------------------------------ */
	/*  Derived helpers                                                     */
	/* ------------------------------------------------------------------ */

	const hasFolder = project.workdriveFolderId !== null;
	const isAnyOperationInFlight = isSyncing || isSyncingEntities || isChangingFolder || isUnlinking || isReingesting || isRetryingFailed;
	const failedFileCount = kbStats?.failed ?? 0;

	/**
	 * Returns a CSS modifier class for the budget bar and text based on
	 * the status field from the API.
	 */
	function budgetStatusClass(status: CallBudgetStatus["status"]): string {
		if (status === "exhausted") return "critical";
		if (status === "warning") return "warning";
		return "normal";
	}

	/* ------------------------------------------------------------------ */
	/*  Render                                                              */
	/* ------------------------------------------------------------------ */

	const panel = (
		<>
			<div
				ref={panelRef}
				className="project-settings-panel"
				role="dialog"
				aria-label={`Settings for ${project.name}`}
				style={{
					position: "fixed",
					top: `${top}px`,
					left: `${left}px`,
					width: `${panelWidth}px`,
				}}
			>
				{/* Panel header */}
				<div className="project-settings-panel-header">
					<span className="project-settings-panel-title">Project Settings</span>
					<span className="project-settings-panel-project">{project.name}</span>
				</div>

				{/* -------------------------------------------------------- */}
				{/* Knowledge Base Stats                                       */}
				{/* -------------------------------------------------------- */}
				{project.supabaseId !== null && (
					<div className="project-settings-section">
						<div className="project-settings-section-label">Knowledge Base</div>
						{isLoadingKbStats && kbStats === null ? (
							<div className="project-settings-kb-loading">Loading stats…</div>
						) : kbStats !== null ? (
							<div className="project-settings-kb-card">
								{/* Total file count — headline figure */}
								<div className="project-settings-kb-headline">
									{kbStats.totalFiles} {kbStats.totalFiles === 1 ? "file" : "files"} in knowledge base
								</div>

								{/* Status breakdown */}
								{kbStats.totalFiles > 0 && (
									<div className="project-settings-kb-statuses">
										{kbStats.indexed > 0 && (
											<span className="project-settings-kb-badge project-settings-kb-badge--completed">
												<span className="project-settings-kb-dot project-settings-kb-dot--completed" aria-hidden="true" />
												{kbStats.indexed} indexed
											</span>
										)}
										{kbStats.catalogued > 0 && (
											<span className="project-settings-kb-badge project-settings-kb-badge--catalogued">
												<span className="project-settings-kb-dot project-settings-kb-dot--catalogued" aria-hidden="true" />
												{kbStats.catalogued} catalogued
											</span>
										)}
										{kbStats.processing > 0 && (
											<span className="project-settings-kb-badge project-settings-kb-badge--processing">
												<span className="project-settings-kb-dot project-settings-kb-dot--processing" aria-hidden="true" />
												{kbStats.processing} processing
											</span>
										)}
										{kbStats.pending > 0 && (
											<span className="project-settings-kb-badge project-settings-kb-badge--pending">
												<span className="project-settings-kb-dot project-settings-kb-dot--pending" aria-hidden="true" />
												{kbStats.pending} pending
											</span>
										)}
										{kbStats.failed > 0 && (
											<span className="project-settings-kb-badge project-settings-kb-badge--failed">
												<span className="project-settings-kb-dot project-settings-kb-dot--failed" aria-hidden="true" />
												{kbStats.failed} failed
											</span>
										)}
									</div>
								)}

								{/* Chunk count */}
								{(kbStats.totalChunks ?? 0) > 0 && (
									<div className="project-settings-kb-chunks">
										{(kbStats.totalChunks ?? 0).toLocaleString("en-GB")} text {(kbStats.totalChunks ?? 0) === 1 ? "chunk" : "chunks"} indexed
									</div>
								)}

								{/* File type breakdown */}
								{Object.keys(kbStats.fileTypes).length > 0 && (
									<div className="project-settings-kb-filetypes">
										{Object.entries(kbStats.fileTypes)
											.sort(([, a], [, b]) => b - a)
											.map(([ext, count]) => (
												<span key={ext} className="project-settings-kb-filetype-pill">
													{count} {ext.toUpperCase()}
												</span>
											))}
									</div>
								)}

								{/* Failed file warning */}
								{kbStats.failed > 0 && (
									<div className="project-settings-kb-failed-warning" role="alert">
										<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
											<path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
											<line x1="12" y1="9" x2="12" y2="13"/>
											<line x1="12" y1="17" x2="12.01" y2="17"/>
										</svg>
										{kbStats.failed} {kbStats.failed === 1 ? "file" : "files"} failed to process — use Retry below
									</div>
								)}
							</div>
						) : null}
					</div>
				)}

				{/* -------------------------------------------------------- */}
				{/* Action: Sync Now                                          */}
				{/* -------------------------------------------------------- */}
				<div className="project-settings-section">
					<div className="project-settings-section-label">Documents</div>
					<p className="project-settings-last-synced">
						{lastIngestedAt === null
							? "Never synced"
							: `Last synced: ${formatRelativeTime(lastIngestedAt)}`}
					</p>
					<button
						type="button"
						className="project-settings-action"
						onClick={() => void handleSyncNow()}
						disabled={!hasFolder || isSyncing || isChangingFolder || isUnlinking}
						title={hasFolder ? "Re-index documents from the linked WorkDrive folder" : "No folder linked"}
					>
						<svg className="project-settings-action-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
							<polyline points="1 4 1 10 7 10"/>
							<path d="M3.51 15a9 9 0 1 0 .49-3.51"/>
						</svg>
						{isSyncing ? "Syncing…" : "Sync Now"}
					</button>

					{/* Retry Failed Files — only shown when a folder is linked */}
					{hasFolder && (
						<button
							type="button"
							className="project-settings-action project-settings-action--warning"
							onClick={() => void handleRetryFailed()}
							disabled={isAnyOperationInFlight || failedFileCount === 0}
							title={failedFileCount === 0 ? "No failed files to retry" : "Reset failed documents to pending so they are retried on the next sync"}
						>
							<svg className="project-settings-action-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
								<circle cx="12" cy="12" r="10"/>
								<line x1="12" y1="8" x2="12" y2="12"/>
								<line x1="12" y1="16" x2="12.01" y2="16"/>
							</svg>
							{isRetryingFailed ? "Retrying…" : (
							<>
								Retry Failed Files
								{failedFileCount > 0 && (
									<span className="project-settings-kb-retry-count" aria-label={`${failedFileCount} failed`}>
										{failedFileCount}
									</span>
								)}
							</>
						)}
						</button>
					)}

					{/* Re-ingest All Documents — only shown for admins when a folder is linked */}
					{hasFolder && isAdmin && (
						confirmAction === "reingest" ? (
							<div className="project-settings-confirm">
								<p className="project-settings-confirm-text">
									This will delete all indexed chunks and re-ingest every document. This may take several minutes for large projects.
								</p>
								<div className="project-settings-confirm-actions">
									<button
										type="button"
										className="project-settings-confirm-btn project-settings-confirm-btn--ok"
										onClick={() => void handleReingestConfirm()}
									>
										Re-ingest
									</button>
									<button
										type="button"
										className="project-settings-confirm-btn project-settings-confirm-btn--cancel"
										onClick={() => setConfirmAction(null)}
									>
										Cancel
									</button>
								</div>
							</div>
						) : (
							<button
								type="button"
								className="project-settings-action"
								onClick={handleReingestRequest}
								disabled={isAnyOperationInFlight}
								title="Delete all indexed chunks and re-ingest every document from scratch"
							>
								<svg className="project-settings-action-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
									<path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/>
								</svg>
								{isReingesting ? "Re-ingesting…" : "Re-ingest All Documents"}
							</button>
						)
					)}
				</div>

				{/* -------------------------------------------------------- */}
				{/* Action: Sync Project Data                                 */}
				{/* -------------------------------------------------------- */}
				<div className="project-settings-section">
					<div className="project-settings-section-label">Live Project Data</div>
					<button
						type="button"
						className="project-settings-action project-settings-action--entities"
						onClick={() => void handleSyncEntities()}
						disabled={isAnyOperationInFlight}
						title="Pull the latest tasks, milestones, and issues from Zoho Projects"
					>
						<svg className="project-settings-action-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
							<path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
							<polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
							<line x1="12" y1="22.08" x2="12" y2="12"/>
						</svg>
						{isSyncingEntities ? "Syncing…" : "Sync Project Data"}
					</button>

					{/* Call budget indicator — only shown when calls are being tracked */}
					{callBudget !== null && (
						<div className={`project-settings-budget project-settings-budget--${budgetStatusClass(callBudget.status)}`} aria-label="Webhook call budget">
							<div className="project-settings-budget-label">
								<span>Webhook calls</span>
								<span>{callBudget.count.toLocaleString("en-GB")} / {callBudget.limit.toLocaleString("en-GB")} ({callBudget.percentUsed}%)</span>
							</div>
							<div className="project-settings-budget-bar" role="progressbar" aria-valuenow={callBudget.percentUsed} aria-valuemin={0} aria-valuemax={100} aria-label={`Webhook call budget: ${callBudget.percentUsed}% used`}>
								<div
									className="project-settings-budget-bar-fill"
									style={{ width: `${Math.min(callBudget.percentUsed, 100)}%` }}
								/>
							</div>
						</div>
					)}
				</div>

				{/* -------------------------------------------------------- */}
				{/* Action: Change Folder                                     */}
				{/* -------------------------------------------------------- */}
				<div className="project-settings-section">
					<div className="project-settings-section-label">Folder</div>

					{/* Linked folder name — shown when a folder is linked */}
					{hasFolder && (
						<div className="project-settings-folder-name" aria-label="Linked WorkDrive folder">
						<svg
							className="project-settings-folder-name-icon"
							width="13"
							height="13"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							strokeWidth="2"
							strokeLinecap="round"
							strokeLinejoin="round"
							aria-hidden="true"
						>
							<path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
						</svg>
						<span className="project-settings-folder-name-text">
							{linkedFolderName ?? "Loading…"}
						</span>
					</div>
				)}

					{confirmAction === "change" ? (
						<div className="project-settings-confirm">
							<p className="project-settings-confirm-text">
								This will remove all indexed documents and re-index from the new folder. Continue?
							</p>
							<div className="project-settings-confirm-actions">
								<button
									type="button"
									className="project-settings-confirm-btn project-settings-confirm-btn--ok"
									onClick={handleChangeFolderConfirm}
								>
									Continue
								</button>
								<button
									type="button"
									className="project-settings-confirm-btn project-settings-confirm-btn--cancel"
									onClick={() => setConfirmAction(null)}
								>
									Cancel
								</button>
							</div>
						</div>
					) : (
						<button
							type="button"
							className="project-settings-action"
							onClick={handleChangeFolderRequest}
							disabled={isAnyOperationInFlight}
							title="Choose a different WorkDrive folder for this project"
						>
							<svg className="project-settings-action-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
								<path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
							</svg>
							{isChangingFolder ? "Changing…" : "Change Folder"}
						</button>
					)}
				</div>

				{/* -------------------------------------------------------- */}
				{/* Action: Unlink Folder                                     */}
				{/* -------------------------------------------------------- */}
				<div className="project-settings-section project-settings-section--last">
					{confirmAction === "unlink" ? (
						<div className="project-settings-confirm project-settings-confirm--destructive">
							<p className="project-settings-confirm-text">
								This will remove the folder link and delete all indexed documents. This cannot be undone.
							</p>
							<div className="project-settings-confirm-actions">
								<button
									type="button"
									className="project-settings-confirm-btn project-settings-confirm-btn--destructive"
									onClick={() => void handleUnlinkConfirm()}
								>
									Unlink
								</button>
								<button
									type="button"
									className="project-settings-confirm-btn project-settings-confirm-btn--cancel"
									onClick={() => setConfirmAction(null)}
								>
									Cancel
								</button>
							</div>
						</div>
					) : (
						<button
							type="button"
							className="project-settings-action project-settings-action--destructive"
							onClick={handleUnlinkRequest}
							disabled={isAnyOperationInFlight}
							title="Remove the WorkDrive folder link and delete all indexed documents"
						>
							<svg className="project-settings-action-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
								<polyline points="3 6 5 6 21 6"/>
								<path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
								<path d="M10 11v6"/>
								<path d="M14 11v6"/>
								<path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
							</svg>
							{isUnlinking ? "Unlinking…" : "Unlink Folder"}
						</button>
					)}
				</div>

				{/* -------------------------------------------------------- */}
				{/* Status message                                            */}
				{/* -------------------------------------------------------- */}
				{status && (
					<div className={`project-settings-status project-settings-status--${status.type}`} role="status" aria-live="polite">
						{status.text}
					</div>
				)}
			</div>

			{/* Folder explorer for Change Folder flow */}
			{showFolderExplorer && (
				<FolderExplorerModal
					onSelect={(folderId) => void handleFolderSelected(folderId)}
					onClose={() => {
						setShowFolderExplorer(false);
					}}
				/>
			)}
		</>
	);

	return createPortal(panel, document.body);
}

// =============================================================================
// useProjectSettings — Phase 2 hook extraction
//
// Exposes all state + handlers from ProjectSettingsPanel so that SyncTray and
// the legacy modal variant can share the same logic without duplication.
// =============================================================================

export interface UseProjectSettingsArgs {
	project: EnrichedProject;
	onSyncTriggered: () => void;
	onFolderChanged: () => void;
	onFolderUnlinked: () => void;
	onSyncStarting?: () => void;
	onSyncFailed?: () => void;
}

export interface UseProjectSettingsResult {
	// State
	isSyncing: boolean;
	isSyncingEntities: boolean;
	isChangingFolder: boolean;
	isUnlinking: boolean;
	isReingesting: boolean;
	isRetryingFailed: boolean;
	kbStats: KbStats | null;
	callBudget: CallBudgetStatus | null;
	linkedFolderName: string | null;
	showFolderExplorer: boolean;
	status: { type: "info" | "error" | "success"; text: string } | null;
	confirmAction: ConfirmAction;
	// Handlers
	handleSyncNow: () => Promise<void>;
	handleSyncEntities: () => Promise<void>;
	handleRetryFailed: () => Promise<void>;
	handleChangeFolder: () => void;
	handleFolderSelected: (folderId: string, folderName: string) => Promise<void>;
	handleUnlink: () => Promise<void>;
	handleReingestAll: () => Promise<void>;
	setConfirmAction: (action: ConfirmAction) => void;
	setShowFolderExplorer: (show: boolean) => void;
}

/**
 * Extracts the project-settings state machine from ProjectSettingsPanel into a
 * reusable hook. Both the legacy modal variant (ProjectSettingsPanel) and the
 * new SyncTray consume this hook — one source of truth for all async handlers.
 *
 * All in-flight guards (isSyncing, isUnlinking, etc.) are preserved exactly as
 * they existed in the original component. Callbacks are memoised with useCallback
 * so that SyncTrayActions does not re-render unnecessarily.
 */
export function useProjectSettings({
	project,
	onSyncTriggered,
	onFolderChanged,
	onFolderUnlinked,
	onSyncStarting,
	onSyncFailed,
}: UseProjectSettingsArgs): UseProjectSettingsResult {
	const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null);
	const [isSyncing, setIsSyncing] = useState(false);
	const [isSyncingEntities, setIsSyncingEntities] = useState(false);
	const [callBudget, setCallBudget] = useState<CallBudgetStatus | null>(null);
	const [kbStats, setKbStats] = useState<KbStats | null>(null);
	const [isChangingFolder, setIsChangingFolder] = useState(false);
	const [isUnlinking, setIsUnlinking] = useState(false);
	const [isReingesting, setIsReingesting] = useState(false);
	const [isRetryingFailed, setIsRetryingFailed] = useState(false);
	const [showFolderExplorer, setShowFolderExplorer] = useState(false);
	const [linkedFolderName, setLinkedFolderName] = useState<string | null>(null);
	const [status, setStatus] = useState<{ type: "info" | "error" | "success"; text: string } | null>(null);

	// useRef booleans are set synchronously before the async fetch begins.
	// This means rapid back-to-back calls (before React processes the first
	// setState) still see the correct in-flight status — unlike reading the
	// state variable which may be stale across multiple synchronous invocations
	// within the same render cycle (§4.5 P-blocker, Suite P tests).
	const isSyncingRef = useRef(false);
	const isSyncingEntitiesRef = useRef(false);
	const isChangingFolderRef = useRef(false);
	const isUnlinkingRef = useRef(false);
	const isReingestionRef = useRef(false);
	const isRetryingFailedRef = useRef(false);

	// Stable ref for fetchKbStats so it can be called from callbacks without
	// being in their dependency arrays (prevents stale-closure issues).
	const fetchKbStatsRef = useRef<() => Promise<void>>(async () => {});

	// Fetch KB stats
	const fetchKbStats = useCallback(async (): Promise<void> => {
		if (!project.supabaseId) return;
		try {
			const response = await apiGet(`/api/projects/${project.id}/kb-stats`);
			if (response.ok) {
				const data = await response.json() as KbStats;
				setKbStats(data);
			}
		} catch {
			// Non-critical — silently ignore
		}
	}, [project.id, project.supabaseId]);

	// Keep the ref in sync with the stable callback
	useEffect(() => {
		fetchKbStatsRef.current = fetchKbStats;
	}, [fetchKbStats]);

	// Fetch call budget on mount
	useEffect(() => {
		async function fetchCallBudget(): Promise<void> {
			try {
				const response = await apiGet("/api/webhooks/budget");
				if (response.ok) {
					const data = await response.json() as CallBudgetStatus;
					if (data.count > 0) setCallBudget(data);
				}
			} catch {
				// Non-critical
			}
		}
		void fetchCallBudget();
	}, []);

	// Fetch KB stats when project changes
	useEffect(() => {
		void fetchKbStats();
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [project.id]);

	// Fetch linked folder name
	useEffect(() => {
		if (!project.workdriveFolderId) {
			setLinkedFolderName(null);
			return;
		}
		async function fetchFolderName(): Promise<void> {
			if (!project.workdriveFolderId) return;
			try {
				const response = await apiGet(`/api/workdrive/folder-info/${project.workdriveFolderId}`);
				if (response.ok) {
					const data = await response.json() as { id: string; name: string };
					setLinkedFolderName(data.name);
				}
			} catch {
				// Non-critical
			}
		}
		void fetchFolderName();
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [project.workdriveFolderId]);

	const handleSyncNow = useCallback(async (): Promise<void> => {
		if (isSyncingRef.current) return;
		isSyncingRef.current = true;
		setIsSyncing(true);
		setStatus(null);
		try {
			onSyncStarting?.();
			// POST to /ingest — the canonical worker endpoint for triggering document ingestion.
			// Both the legacy ProjectSettingsPanel component and this hook must use the same path.
			const response = await apiPost(`/api/projects/${project.id}/ingest`, {});
			if (response.status === 202 || response.ok) {
				setStatus({ type: "success", text: "Sync started" });
				void fetchKbStatsRef.current();
				setTimeout(() => { onSyncTriggered(); }, 1200);
			} else {
				const errorData = await response.json().catch(() => null) as { message?: string } | null;
				onSyncFailed?.();
				setStatus({ type: "error", text: errorData?.message ?? `Sync failed (${response.status})` });
			}
		} catch {
			onSyncFailed?.();
			setStatus({ type: "error", text: "Failed to connect to the server." });
		} finally {
			isSyncingRef.current = false;
			setIsSyncing(false);
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [project.id, onSyncStarting, onSyncFailed, onSyncTriggered]);

	const handleRetryFailed = useCallback(async (): Promise<void> => {
		if (isRetryingFailedRef.current) return;
		isRetryingFailedRef.current = true;
		setIsRetryingFailed(true);
		setStatus(null);
		try {
			const response = await apiPost(`/api/projects/${project.id}/retry-failed`, {});
			if (response.ok || response.status === 202) {
				const data = await response.json().catch(() => null) as { retriedCount?: number; skippedPermanent?: number; message?: string } | null;
				const count = data?.retriedCount ?? 0;
				const skipped = data?.skippedPermanent ?? 0;
				const skippedSuffix = skipped > 0 ? ` (${skipped} permanent failure${skipped === 1 ? "" : "s"} skipped)` : "";
				setStatus({
					type: "success",
					text: count === 0
						? (data?.message ?? "No failed files to retry")
						: `${count} ${count === 1 ? "file" : "files"} queued for retry${skippedSuffix}`,
				});
				void fetchKbStatsRef.current();
				setTimeout(() => { setStatus(null); }, 3000);
			} else {
				const errorData = await response.json().catch(() => null) as { message?: string } | null;
				setStatus({ type: "error", text: errorData?.message ?? `Retry failed (${response.status})` });
			}
		} catch {
			setStatus({ type: "error", text: "Failed to connect to the server." });
		} finally {
			isRetryingFailedRef.current = false;
			setIsRetryingFailed(false);
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [project.id]);

	const handleSyncEntities = useCallback(async (): Promise<void> => {
		if (isSyncingEntitiesRef.current) return;
		isSyncingEntitiesRef.current = true;
		setIsSyncingEntities(true);
		setStatus(null);
		try {
			const response = await apiPost(`/api/projects/${project.id}/sync-entities`, {});
			if (response.ok || response.status === 202) {
				setStatus({ type: "success", text: "Project data synced" });
				setTimeout(() => { setStatus(null); }, 2500);
			} else {
				const errorData = await response.json().catch(() => null) as { message?: string } | null;
				setStatus({ type: "error", text: errorData?.message ?? `Sync failed (${response.status})` });
			}
		} catch {
			setStatus({ type: "error", text: "Failed to connect to the server." });
		} finally {
			isSyncingEntitiesRef.current = false;
			setIsSyncingEntities(false);
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [project.id]);

	const handleChangeFolder = useCallback((): void => {
		setConfirmAction("change");
		setStatus(null);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [project.id]);

	const handleFolderSelected = useCallback(async (folderId: string): Promise<void> => {
		if (isChangingFolderRef.current) return;
		isChangingFolderRef.current = true;
		setShowFolderExplorer(false);
		setIsChangingFolder(true);
		setStatus({ type: "info", text: "Updating folder link…" });
		try {
			const putResponse = await apiPut(`/api/projects/${project.id}/folder`, { folderId });
			if (!putResponse.ok) {
				const errorData = await putResponse.json().catch(() => null) as { message?: string } | null;
				setStatus({ type: "error", text: errorData?.message ?? `Failed to update folder (${putResponse.status})` });
				return;
			}
			setStatus({ type: "info", text: "Removing old documents…" });
			const deleteResponse = await apiDelete(`/api/projects/${project.id}/documents`);
			if (!deleteResponse.ok) {
				const errorData = await deleteResponse.json().catch(() => null) as { message?: string } | null;
				setStatus({ type: "error", text: errorData?.message ?? "Failed to remove old documents." });
				return;
			}
			setStatus({ type: "info", text: "Starting re-ingestion…" });
			const ingestResponse = await apiPost(`/api/projects/${project.id}/ingest`, {});
			if (!ingestResponse.ok && ingestResponse.status !== 202) {
				const errorData = await ingestResponse.json().catch(() => null) as { message?: string } | null;
				setStatus({ type: "error", text: errorData?.message ?? "Folder updated but ingestion failed to start." });
				return;
			}
			setStatus({ type: "success", text: "Folder changed — re-indexing started" });
			setTimeout(() => { onFolderChanged(); }, 1200);
		} catch {
			setStatus({ type: "error", text: "Failed to connect to the server." });
		} finally {
			isChangingFolderRef.current = false;
			setIsChangingFolder(false);
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [project.id, onFolderChanged]);

	const handleUnlink = useCallback(async (): Promise<void> => {
		if (isUnlinkingRef.current) return;
		isUnlinkingRef.current = true;
		setIsUnlinking(true);
		setStatus({ type: "info", text: "Removing folder link…" });
		try {
			const response = await apiDelete(`/api/projects/${project.id}/folder`);
			if (response.ok) {
				setStatus({ type: "success", text: "Folder unlinked" });
				setTimeout(() => { onFolderUnlinked(); }, 1000);
			} else {
				const errorData = await response.json().catch(() => null) as { message?: string } | null;
				setStatus({ type: "error", text: errorData?.message ?? `Failed to unlink folder (${response.status})` });
			}
		} catch {
			setStatus({ type: "error", text: "Failed to connect to the server." });
		} finally {
			isUnlinkingRef.current = false;
			setIsUnlinking(false);
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [project.id, onFolderUnlinked]);

	const handleReingestAll = useCallback(async (): Promise<void> => {
		if (isReingestionRef.current) return;
		isReingestionRef.current = true;
		setIsReingesting(true);
		setStatus({ type: "info", text: "Starting re-ingestion…" });
		try {
			const response = await apiPost(`/api/projects/${project.id}/rebuild-knowledge-base`, {});
			if (response.ok || response.status === 202) {
				setStatus({ type: "success", text: "Re-ingestion started — this may take several minutes" });
				setTimeout(() => { onSyncTriggered(); }, 1500);
				setTimeout(() => { setStatus(null); }, 3500);
			} else {
				const errorData = await response.json().catch(() => null) as { message?: string } | null;
				setStatus({ type: "error", text: errorData?.message ?? `Re-ingestion failed (${response.status})` });
			}
		} catch {
			setStatus({ type: "error", text: "Failed to connect to the server." });
		} finally {
			isReingestionRef.current = false;
			setIsReingesting(false);
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [project.id, onSyncTriggered]);

	return {
		isSyncing,
		isSyncingEntities,
		isChangingFolder,
		isUnlinking,
		isReingesting,
		isRetryingFailed,
		kbStats,
		callBudget,
		linkedFolderName,
		showFolderExplorer,
		status,
		confirmAction,
		handleSyncNow,
		handleSyncEntities,
		handleRetryFailed,
		handleChangeFolder,
		handleFolderSelected,
		handleUnlink,
		handleReingestAll,
		setConfirmAction,
		setShowFolderExplorer,
	};
}
